import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { 
  FaRegLightbulb, 
  FaCompass, 
  FaVial, 
  FaChartLine, 
  FaEnvelope, 
  FaRobot, 
  FaCog,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaExternalLinkAlt
} from "react-icons/fa";
import { dismissRecommendation } from "../agentSlice";

const categoryMeta = {
  nudge: { icon: FaRegLightbulb, color: "text-purple-500 bg-purple-50 border-purple-200", route: "/personas" },
  landing: { icon: FaCompass, color: "text-emerald-500 bg-emerald-50 border-emerald-200", route: "/landing-optimization" },
  abtest: { icon: FaVial, color: "text-indigo-500 bg-indigo-50 border-indigo-200", route: "/abtest" },
  traffic: { icon: FaChartLine, color: "text-cyan-500 bg-cyan-50 border-cyan-200", route: "/traffic" },
  email: { icon: FaEnvelope, color: "text-teal-500 bg-teal-50 border-teal-200", route: "/offline-emails" },
  chatbot: { icon: FaRobot, color: "text-pink-500 bg-pink-50 border-pink-200", route: "/chatbot" },
  setup: { icon: FaCog, color: "text-blue-500 bg-blue-50 border-blue-200", route: "/settings" },
};

const priorityMeta = {
  critical: "bg-rose-50 text-rose-950 font-bold border border-rose-300",
  high: "bg-orange-50 text-orange-950 font-bold border border-orange-300",
  medium: "bg-amber-50 text-amber-950 font-bold border border-amber-300",
  low: "bg-slate-50 text-slate-800 font-semibold border border-slate-300",
};

const safeRender = (val) => {
  if (typeof val === "string" || typeof val === "number") return val;
  if (typeof val === "object" && val !== null) return val.title || val.text || val.recommendation || JSON.stringify(val);
  return "";
};

function getDoItRoute(recommendation) {
  const searchText = [
    recommendation.title || "",
    recommendation.recommendation || "",
    recommendation.evidence || "",
  ].join(" ").toLowerCase();

  // Priority 1: Specific feature keywords → exact dashboard routes
  const keywordRoutes = [
    // Session Replay
    { keywords: ["session replay", "session-replay", "replay"], route: "/session-replay" },
    // Heatmap & Scroll
    { keywords: ["heatmap", "scrollmap", "scroll depth", "click map", "click density", "above the fold", "move section", "rearrange"], route: "/heatmap" },
    // Nudges
    { keywords: ["nudge", "nudges", "nudge campaign", "popup", "pop-up", "coupon"], route: "/custom-nudge" },
    // Personas / Nudge Performance
    { keywords: ["persona", "exploratory", "missed opportunity", "visitor segment"], route: "/personas" },
    // Nudge Performance (coupon-wise)
    { keywords: ["nudge performance", "add-to-cart rate", "coupon performance"], route: "/personas/performance" },
    // A/B Testing
    { keywords: ["ab test", "a/b test", "abtest", "split test", "variant", "experiment"], route: "/abtest" },
    // Email
    { keywords: ["email campaign", "email integration", "email capture", "email follow", "email marketing"], route: "/offline-emails" },
    // Chatbot
    { keywords: ["chatbot", "chat bot", "live chat"], route: "/chatbot" },
    // SEO / Organic
    { keywords: ["seo", "meta title", "meta description", "keyword ranking", "organic search", "search visibility"], route: "/seo-funnel" },
    // Traffic / Ads
    { keywords: ["traffic source", "google traffic", "facebook traffic", "retarget", "ad spend", "ad campaign", "ad budget"], route: "/traffic" },
    // Landing Page
    { keywords: ["landing page", "homepage layout", "page layout", "page engagement", "exit rate", "bounce rate"], route: "/landing-optimization" },
    // User Journey / Funnel
    { keywords: ["user journey", "user path", "drop-off", "dropoff", "funnel", "conversion path"], route: "/cohort-insight" },
    // Booking
    { keywords: ["booking button", "book now", "booking process", "checkout", "booking page"], route: "/booking/stats" },
    // Mobile optimization → heatmap (best tool to analyze)
    { keywords: ["mobile experience", "mobile layout", "mobile-first", "mobile optimization", "mobile users"], route: "/heatmap" },
    // Ads leakage
    { keywords: ["ads leakage", "ad leakage", "wasted ad spend"], route: "/ads-leakage" },
    // Integration
    { keywords: ["integration", "brevo", "mailchimp", "klaviyo", "whatsapp", "hubspot"], route: "/integration" },
    // Data source / tracking setup
    { keywords: ["tracking", "data source", "install script", "pixel"], route: "/data-source" },
  ];

  for (const { keywords, route } of keywordRoutes) {
    for (const keyword of keywords) {
      if (searchText.includes(keyword)) {
        return route;
      }
    }
  }

  // Priority 2: Category-based fallback using actual routes
  const categoryRoutes = {
    nudge: "/custom-nudge",
    landing: "/landing-optimization",
    abtest: "/abtest",
    traffic: "/traffic",
    email: "/offline-emails",
    chatbot: "/chatbot",
    setup: "/settings",
    experiment: "/abtest",
  };

  return categoryRoutes[recommendation.category] || "/";
}

// Smart button labels matching the destination
function getDoItLabel(recommendation, route) {
  const routeLabels = {
    "/session-replay": "View Replays",
    "/heatmap": "View Heatmap",
    "/custom-nudge": "Edit Nudges",
    "/personas": "View Personas",
    "/personas/performance": "Nudge Stats",
    "/abtest": "Set Up Test",
    "/offline-emails": "Set Up Email",
    "/chatbot": "Configure Bot",
    "/chatbot-analytics": "Bot Analytics",
    "/seo-funnel": "View SEO Data",
    "/traffic": "View Traffic",
    "/landing-optimization": "Optimize Page",
    "/cohort-insight": "View Journeys",
    "/booking/stats": "Booking Stats",
    "/ads-leakage": "View Leakage",
    "/integration": "Integrations",
    "/data-source": "Data Sources",
    "/settings": "Go to Settings",
  };
  return routeLabels[route] || "Do It";
}

export const RecommendationCard = ({ recommendation, clientId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const { id, category, priority, title, recommendation: text, evidence, expectedImpact, experiment } = recommendation;

  const cat = categoryMeta[category] || categoryMeta.setup;
  const CategoryIcon = cat.icon;

  const route = getDoItRoute(recommendation);
  const label = getDoItLabel(recommendation, route);

  const handleDoIt = (e) => {
    e.stopPropagation();
    navigate(route);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    dispatch(dismissRecommendation({ collectionName: clientId, recommendationId: id }));
  };

  return (
    <div 
      onClick={() => setIsExpanded(!isExpanded)}
      className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col mb-4 cursor-pointer"
    >
      {/* Top Banner / Details */}
      <div className="p-4 flex items-start justify-between gap-3">
        {/* Category Icon */}
        <div className={`p-2.5 rounded-lg border ${cat.color} flex items-center justify-center shrink-0`}>
          <CategoryIcon className="text-xl" />
        </div>

        {/* Title & priority */}
        <div className="flex-grow min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${priorityMeta[priority] || priorityMeta.low}`}>
              {priority}
            </span>
            {expectedImpact && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-50 text-emerald-900 border border-emerald-200">
                🚀 {safeRender(expectedImpact)}
              </span>
            )}
            <span className="text-xs text-slate-500 capitalize font-medium">{category}</span>
          </div>
          <h4 className="font-bold text-slate-800 text-sm md:text-base leading-snug">
            {safeRender(title)}
          </h4>
        </div>

        {/* Dismiss Button */}
        <button 
          onClick={handleDismiss}
          className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"
          title="Dismiss recommendation"
        >
          <FaTimes className="text-xs" />
        </button>
      </div>

      {/* Main Body content */}
      <div className="px-4 pb-3 flex-grow">
        <p className={`text-slate-700 text-sm mb-2.5 leading-relaxed ${isExpanded ? "" : "line-clamp-3"}`}>
          {safeRender(text)}
        </p>
        
        {evidence && (
          <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 mb-2">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">
              Evidence / Metric
            </span>
            <p className="text-slate-700 text-xs italic">
              {safeRender(evidence)}
            </p>
          </div>
        )}
      </div>

      {/* Expandable Section */}
      {isExpanded && (
        <div className="px-4 py-3 bg-slate-50/50 border-t border-slate-100 space-y-3 transition-all">
          {expectedImpact && (
            <div>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">
                Expected Impact
              </span>
              <p className="text-emerald-950 font-bold text-xs">
                {safeRender(expectedImpact)}
              </p>
            </div>
          )}
          {experiment && (
            <div>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">
                Experiment Suggestion
              </span>
              <p className="text-slate-700 text-xs leading-relaxed">
                {safeRender(experiment)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Action Area */}
      <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors"
        >
          {isExpanded ? (
            <>
              Hide Details <FaChevronUp className="text-[10px]" />
            </>
          ) : (
            <>
              Show Details <FaChevronDown className="text-[10px]" />
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleDoIt}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-semibold shadow-sm hover:shadow transition-all duration-200"
        >
          {label} <FaExternalLinkAlt className="text-[9px]" />
        </button>
      </div>
    </div>
  );
};
export default RecommendationCard;
